import type { BrowserProfileId } from './browserProfiles';

/**
 * How a fixture cell earned its value. `assumed` is never "verified against reality".
 * `manual` means a person ran the scenario in the real browser and recorded what they saw in `note`.
 */
export type VerifiedBy = 'bcd' | 'real-engine' | 'manual' | 'assumed';

export type BehaviorEngine = 'chromium' | 'firefox' | 'webkit';

export type BehaviorFixtureCell = {
  engine: BehaviorEngine;
  /** Narrows the cell to one browser profile; it then wins over the engine-wide cell. */
  profile?: BrowserProfileId;
  claim: string;
  value: boolean | string | number;
  verifiedBy: VerifiedBy;
  /** Required for `manual`: date, browser + version, and what was observed. */
  note?: string;
};

/** Claims a real-engine probe actually exists for. A `real-engine` cell MUST be listed here. */
export const PROBED_CLAIMS: ReadonlySet<string> = new Set(['permissionsQueryUsable']);

/**
 * The single source of "what we believe each engine does", tagged with provenance. The fake
 * browser (`devices/testing/fakeMediaEnvironment`) consumes this; the guard test forbids any
 * `real-engine` cell without a probe, and pins the `assumed` set so it cannot grow silently.
 *
 * When a real-browser check disagrees with a cell, change the value, set `verifiedBy: 'manual'`
 * with a `note`, and let the scenario matrix show what that breaks. See `devices/testing/README.md`.
 */
export const BEHAVIOR_FIXTURES: readonly BehaviorFixtureCell[] = [
  // --- Does `permissions.query` work for camera/microphone at all? ---
  { engine: 'chromium', claim: 'permissionsQueryUsable', value: true, verifiedBy: 'assumed' },
  { engine: 'firefox', claim: 'permissionsQueryUsable', value: true, verifiedBy: 'real-engine' },
  { engine: 'webkit', claim: 'permissionsQueryUsable', value: true, verifiedBy: 'assumed' },

  // --- Can `getUserMedia` open the prompt without a user gesture? ---
  {
    engine: 'chromium',
    claim: 'requiresUserGesture',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-09, Chrome macOS: no click needed: the input-output sample prompted on load, and getUserMedia from a 15s setTimeout after a console call (activation expired) also prompted',
  },
  {
    engine: 'firefox',
    claim: 'requiresUserGesture',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-09, Firefox macOS: no click needed: the input-output sample prompted on load, and getUserMedia from a 15s setTimeout after a console call (activation expired) also prompted',
  },
  // WebKit-in-Playwright is not real Safari/iOS, so these mobile claims cannot be verified
  // in CI — they stay `assumed` and are watched only by production telemetry.
  { engine: 'webkit', claim: 'requiresUserGesture', value: true, verifiedBy: 'assumed' },
  { engine: 'webkit', claim: 'permissionSemantics', value: 'one-time', verifiedBy: 'assumed' },

  // --- Does `permissions.query` ever answer 'denied'? ---
  {
    engine: 'chromium',
    claim: 'reportsPermissionDenial',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Chrome macOS (webrtc.github.io gum sample): after Block, permissions.query read denied',
  },
  {
    engine: 'firefox',
    claim: 'reportsPermissionDenial',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Firefox macOS (webrtc.github.io gum sample): Block without "Remember": query read denied',
  },
  // Also unverifiable in CI (Playwright's WebKit isn't real Safari/iOS), but production telemetry
  // is emphatic: of 48 `devices.microphone.unavailable` actions on iOS over 30 days, 47 classified
  // as `dismissed` — only reachable when `permissions.query` answered 'prompt' after a
  // NotAllowedError. Every other OS shows a normal denied/system_denied mix. iOS's native
  // permission alert has no dismiss affordance (Allow / Don't Allow only), so those were real
  // denials wearing the wrong label. See HNS-234.
  { engine: 'webkit', claim: 'reportsPermissionDenial', value: false, verifiedBy: 'assumed' },

  // --- Can the user close the prompt without answering it? ---
  { engine: 'chromium', claim: 'canDismissPrompt', value: true, verifiedBy: 'assumed' },
  {
    engine: 'firefox',
    claim: 'canDismissPrompt',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-09, Firefox macOS (webrtc.github.io gum sample): the prompt cannot be closed unanswered; Esc counts as Block',
  },
  // Safari and iOS show Allow / Don't Allow only (see HNS-234 above).
  { engine: 'webkit', claim: 'canDismissPrompt', value: false, verifiedBy: 'assumed' },

  // --- How many dismissals before the browser blocks the site by itself (0 = never)? ---
  {
    engine: 'chromium',
    claim: 'dismissalsBeforeAutoBlock',
    value: 3,
    verifiedBy: 'manual',
    note: '2026-10-09, Chrome macOS (webrtc.github.io gum sample): closed the prompt three times; the fourth Open camera showed no prompt',
  },
  {
    engine: 'firefox',
    claim: 'dismissalsBeforeAutoBlock',
    value: 0,
    verifiedBy: 'manual',
    note: '2026-10-09, Firefox macOS (webrtc.github.io gum sample): no dismissal exists (Esc is Block), so there is nothing to count',
  },
  { engine: 'webkit', claim: 'dismissalsBeforeAutoBlock', value: 0, verifiedBy: 'assumed' },

  // --- Does `permissions.query` report that automatic block? ---
  // No: Chrome blocks silently. The site sees a NotAllowedError with no prompt while the
  // permission still reads 'prompt', which looks exactly like a dismissal it could re-ask.
  {
    engine: 'chromium',
    claim: 'autoBlockVisibleToQuery',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-09, Chrome macOS (webrtc.github.io gum sample): after the third dismissal and the silent fourth attempt, query still read prompt',
  },
  // Neither engine blocks on dismissal (dismissalsBeforeAutoBlock is 0), so this never applies.
  { engine: 'firefox', claim: 'autoBlockVisibleToQuery', value: false, verifiedBy: 'assumed' },
  { engine: 'webkit', claim: 'autoBlockVisibleToQuery', value: false, verifiedBy: 'assumed' },

  // --- Does a decision survive a page reload? ---
  {
    engine: 'chromium',
    claim: 'grantPersistsAcrossReload',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Chrome macOS: Allow, reload, Open camera: not asked again. Chrome also offers "Allow this time", which does not survive a reload (not modelled yet)',
  },
  {
    engine: 'chromium',
    claim: 'denialPersistsAcrossReload',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Chrome macOS (webrtc.github.io gum sample): Block, reload, Open camera: no prompt, query still denied',
  },
  // Firefox decisions are temporary unless the user ticks "Remember this decision": a temporary
  // Allow lasts until the page is closed, a temporary Block only until the next reload.
  {
    engine: 'firefox',
    claim: 'grantPersistsAcrossReload',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Firefox macOS (webrtc.github.io gum sample): Allow without "Remember" shows "Allowed temporarily" and survives a reload',
  },
  {
    engine: 'firefox',
    claim: 'denialPersistsAcrossReload',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-09, Firefox macOS (webrtc.github.io gum sample): Block without "Remember", reload, Open camera: prompted again',
  },
  { engine: 'webkit', claim: 'grantPersistsAcrossReload', value: false, verifiedBy: 'assumed' },
  { engine: 'webkit', claim: 'denialPersistsAcrossReload', value: false, verifiedBy: 'assumed' },

  // --- Does a grant survive quitting and reopening the browser (or closing and reopening the page)? ---
  {
    engine: 'chromium',
    claim: 'grantPersistsAcrossRestart',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Chrome macOS: Allow, then two Quit & Reopen cycles for an OS block: never asked again',
  },
  {
    engine: 'firefox',
    claim: 'grantPersistsAcrossRestart',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-09, Firefox macOS (webrtc.github.io gum sample): a temporary Allow survived reloads, but closing and reopening the page prompted again',
  },
  { engine: 'webkit', claim: 'grantPersistsAcrossRestart', value: false, verifiedBy: 'assumed' },

  // --- What `getUserMedia` rejects with when the OS (not the browser) blocks the device ---
  {
    engine: 'chromium',
    claim: 'osDenialError',
    value: 'NotAllowedError',
    verifiedBy: 'manual',
    note: '2026-10-09, Chrome macOS (webrtc.github.io gum sample): camera off in Privacy & Security: NotAllowedError "Permission denied by system" while query read granted',
  },
  // `mapAcquisitionError` already encodes this one (NotFound + permission + device present).
  {
    engine: 'firefox',
    claim: 'osDenialError',
    value: 'NotFoundError',
    verifiedBy: 'manual',
    note: '2026-10-09, Firefox macOS (webrtc.github.io gum sample): camera off in Privacy & Security, after a restart: Allow, then NotFoundError, query read granted; no address-bar indicator',
  },
  { engine: 'webkit', claim: 'osDenialError', value: 'NotAllowedError', verifiedBy: 'assumed' },

  // --- What `getUserMedia` does when no device of the kind exists ---
  { engine: 'chromium', claim: 'noDeviceBehaviour', value: 'NotFoundError', verifiedBy: 'assumed' },
  { engine: 'firefox', claim: 'noDeviceBehaviour', value: 'NotFoundError', verifiedBy: 'assumed' },
  // The reason `acquireDevice` races getUserMedia against a 10s timeout.
  { engine: 'webkit', claim: 'noDeviceBehaviour', value: 'hang', verifiedBy: 'assumed' },

  // --- Are audio outputs listed by `enumerateDevices`? ---
  {
    engine: 'chromium',
    claim: 'enumeratesAudioOutput',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Chrome macOS: enumerateDevices listed 1 audiooutput',
  },
  {
    engine: 'firefox',
    claim: 'enumeratesAudioOutput',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-11, Firefox macOS: listed 3 audiooutput once the microphone was allowed (0 on 2026-10-09 with only the camera allowed)',
  },
  { engine: 'webkit', claim: 'enumeratesAudioOutput', value: false, verifiedBy: 'assumed' },

  // --- Are outputs listed only once the MICROPHONE is allowed (not the camera)? ---
  {
    engine: 'chromium',
    claim: 'audioOutputNeedsMicrophone',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-09, Chrome macOS: listed 1 audiooutput with only the camera allowed',
  },
  {
    engine: 'firefox',
    claim: 'audioOutputNeedsMicrophone',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09 and 2026-10-11, Firefox macOS: 0 audiooutput with only the camera allowed, 3 once the microphone was',
  },
  {
    engine: 'webkit',
    claim: 'audioOutputNeedsMicrophone',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-11, Safari macOS: listed no audiooutput even with both allowed, so the question never arises',
  },

  // --- Does the list carry a separate "Default - <name>" entry (deviceId `default`) per audio kind? ---
  // `mapDevicesForOurApp` folds it into the real device and moves that device first, so the stored
  // list always starts with the system default.
  {
    engine: 'chromium',
    claim: 'listsDefaultAudioEntry',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-11, Chrome macOS: "Default - Logitech BRIO (046d:085e)" and "Default - DELL S2721DGF (HDMI)" beside the real entries',
  },
  {
    engine: 'firefox',
    claim: 'listsDefaultAudioEntry',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-11, Firefox macOS: no default entry; the system default microphone is listed first',
  },
  { engine: 'webkit', claim: 'listsDefaultAudioEntry', value: false, verifiedBy: 'assumed' },
  {
    engine: 'webkit',
    profile: 'safari-macos',
    claim: 'listsDefaultAudioEntry',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-11, Safari macOS: no default entry; the system default microphone is listed first',
  },

  // --- Must the browser be quit and reopened before an OS privacy change takes effect? ---
  // macOS asks the app to "Quit & Reopen" when its camera/mic access changes, so a user who fixes
  // an OS block comes back to a fresh page, never to the same page regaining focus.
  { engine: 'chromium', claim: 'osChangeRequiresBrowserRestart', value: false, verifiedBy: 'assumed' },
  {
    engine: 'chromium',
    profile: 'chrome-macos',
    claim: 'osChangeRequiresBrowserRestart',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Chrome macOS (webrtc.github.io gum sample): turning the camera off, and back on, in Privacy & Security changed nothing until Chrome was quit and reopened',
  },
  {
    engine: 'firefox',
    claim: 'osChangeRequiresBrowserRestart',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Firefox macOS (webrtc.github.io gum sample): turning the camera off changed nothing until Firefox was quit and reopened',
  },
  { engine: 'webkit', claim: 'osChangeRequiresBrowserRestart', value: false, verifiedBy: 'assumed' },

  // --- Does plugging/unplugging fire `devicechange`? ---
  { engine: 'chromium', claim: 'firesDeviceChange', value: true, verifiedBy: 'assumed' },
  // `usePollForDeviceListChanges` polls on Android Chrome because the event never arrives.
  { engine: 'chromium', profile: 'android-chrome', claim: 'firesDeviceChange', value: false, verifiedBy: 'assumed' },
  { engine: 'firefox', claim: 'firesDeviceChange', value: true, verifiedBy: 'assumed' },
  { engine: 'webkit', claim: 'firesDeviceChange', value: true, verifiedBy: 'assumed' },

  // --- Does hiding the page end live capture tracks? ---
  { engine: 'chromium', claim: 'endsTracksWhenBackgrounded', value: false, verifiedBy: 'assumed' },
  { engine: 'firefox', claim: 'endsTracksWhenBackgrounded', value: false, verifiedBy: 'assumed' },
  { engine: 'webkit', claim: 'endsTracksWhenBackgrounded', value: false, verifiedBy: 'assumed' },
  { engine: 'webkit', profile: 'ios-safari', claim: 'endsTracksWhenBackgrounded', value: true, verifiedBy: 'assumed' },
  { engine: 'webkit', profile: 'ios-chrome', claim: 'endsTracksWhenBackgrounded', value: true, verifiedBy: 'assumed' },

  // ---------------------------------------------------------------- Safari macOS (manual, 2026-10-09)
  // Safari on macOS differs enough from iOS that these narrow to the profile; iOS stays engine-wide.
  {
    engine: 'webkit',
    profile: 'safari-macos',
    claim: 'canDismissPrompt',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Safari macOS (webrtc.github.io gum sample): Esc closes the prompt; query stays prompt',
  },
  {
    engine: 'webkit',
    profile: 'safari-macos',
    claim: 'dismissalsBeforeAutoBlock',
    value: 3,
    verifiedBy: 'manual',
    note: '2026-10-09, Safari macOS (webrtc.github.io gum sample): after three dismissals Open camera no longer prompted',
  },
  {
    engine: 'webkit',
    profile: 'safari-macos',
    claim: 'autoBlockVisibleToQuery',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-09, Safari macOS (webrtc.github.io gum sample): while blocked, query read prompt for camera and microphone',
  },
  {
    engine: 'webkit',
    profile: 'safari-macos',
    claim: 'autoBlockClearedBy',
    value: 'restart',
    verifiedBy: 'manual',
    note: '2026-10-09, Safari macOS (webrtc.github.io gum sample): setting the site back to Ask kept the block; quitting and reopening Safari cleared it',
  },
  {
    engine: 'webkit',
    profile: 'safari-macos',
    claim: 'denyIsRemembered',
    value: false,
    verifiedBy: 'manual',
    note: "2026-10-09, Safari macOS (webrtc.github.io gum sample): after Don't Allow, query read prompt and Open camera prompted again",
  },
  {
    engine: 'webkit',
    profile: 'safari-macos',
    claim: 'offersNeverAllow',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Safari macOS (webrtc.github.io gum sample): "Never allow for this website" read denied and stopped further prompts',
  },
  {
    engine: 'webkit',
    profile: 'safari-macos',
    claim: 'reportsPermissionDenial',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Safari macOS (webrtc.github.io gum sample): after Never allow, query read denied',
  },
  {
    engine: 'webkit',
    profile: 'safari-macos',
    claim: 'denialPersistsAcrossReload',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Safari macOS: Never allow still blocked the camera after quitting and reopening Safari',
  },
  {
    engine: 'webkit',
    profile: 'safari-macos',
    claim: 'requiresUserGesture',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-09, Safari macOS: no click needed: the input-output sample prompted on load, and getUserMedia from a 15s setTimeout after a console call (activation expired) also prompted',
  },
  {
    engine: 'webkit',
    profile: 'safari-macos',
    claim: 'enumeratesAudioOutput',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-09, Safari macOS: enumerateDevices listed 0 audiooutput after allowing the camera',
  },
  {
    engine: 'webkit',
    profile: 'safari-macos',
    claim: 'grantPersistsAcrossReload',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-09, Safari macOS (webrtc.github.io gum sample): Allow, reload, Open camera: prompted again',
  },
  {
    engine: 'webkit',
    profile: 'safari-macos',
    claim: 'osCanBlockBrowser',
    value: false,
    verifiedBy: 'manual',
    note: '2026-10-09, Safari macOS (webrtc.github.io gum sample): Safari is not listed under Privacy & Security, Camera',
  },

  // --- Is a plain "Block / Don't Allow" remembered, or does the next request prompt again? ---
  {
    engine: 'chromium',
    claim: 'denyIsRemembered',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Chrome macOS: after Block, query read denied and Open camera did not prompt',
  },
  {
    engine: 'firefox',
    claim: 'denyIsRemembered',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Firefox macOS: after Block without Remember, query read denied until reload',
  },
  { engine: 'webkit', claim: 'denyIsRemembered', value: true, verifiedBy: 'assumed' },

  // --- Is there a separate, permanent "never allow" answer? ---
  // Chrome's Block is already permanent. Firefox's equivalent is Block with "Remember this decision".
  { engine: 'chromium', claim: 'offersNeverAllow', value: false, verifiedBy: 'assumed' },
  { engine: 'firefox', claim: 'offersNeverAllow', value: true, verifiedBy: 'assumed' },
  { engine: 'webkit', claim: 'offersNeverAllow', value: false, verifiedBy: 'assumed' },

  // --- What lifts a silent automatic block? ---
  { engine: 'chromium', claim: 'autoBlockClearedBy', value: 'site-reset', verifiedBy: 'assumed' },
  { engine: 'firefox', claim: 'autoBlockClearedBy', value: 'site-reset', verifiedBy: 'assumed' },
  { engine: 'webkit', claim: 'autoBlockClearedBy', value: 'restart', verifiedBy: 'assumed' },

  // --- Can the OS block this browser from the camera/microphone at all? ---
  { engine: 'chromium', claim: 'osCanBlockBrowser', value: true, verifiedBy: 'assumed' },
  {
    engine: 'chromium',
    profile: 'chrome-macos',
    claim: 'osCanBlockBrowser',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Chrome macOS: Google Chrome is listed under Privacy & Security, Camera',
  },
  {
    engine: 'firefox',
    claim: 'osCanBlockBrowser',
    value: true,
    verifiedBy: 'manual',
    note: '2026-10-09, Firefox macOS: Firefox is listed under Privacy & Security, Camera',
  },
  // iOS has Settings > Safari > Camera, which acts like an OS-level block.
  { engine: 'webkit', claim: 'osCanBlockBrowser', value: true, verifiedBy: 'assumed' },
];

export const assumedCells = (cells: readonly BehaviorFixtureCell[] = BEHAVIOR_FIXTURES): BehaviorFixtureCell[] =>
  cells.filter((cell) => cell.verifiedBy === 'assumed');

/** Every claim any cell covers — the fake refuses to be configured with anything else. */
export const KNOWN_CLAIMS: ReadonlySet<string> = new Set(BEHAVIOR_FIXTURES.map((cell) => cell.claim));

/**
 * The claims for one browser profile: engine-wide cells first, then cells narrowed to the profile.
 * Typed loosely on purpose — the fake reads named claims and the guard tests pin their coverage.
 */
export const claimsForProfile = (
  engine: BehaviorEngine,
  profile: BrowserProfileId,
  cells: readonly BehaviorFixtureCell[] = BEHAVIOR_FIXTURES
): Record<string, boolean | string | number> => {
  const engineWide = cells.filter((cell) => cell.engine === engine && !cell.profile);
  const narrowed = cells.filter((cell) => cell.engine === engine && cell.profile === profile);
  return Object.fromEntries([...engineWide, ...narrowed].map((cell) => [cell.claim, cell.value]));
};
